import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import companyService from '../services/companyService';

interface CompanyState {
  companies: any[];
  suppliers: any[];
  buyers: any[];
  loading: boolean;
  error: string | null;
}

const initialState: CompanyState = {
  companies: [],
  suppliers: [],
  buyers: [],
  loading: false,
  error: null,
};

export const fetchSuppliers = createAsyncThunk(
  'companies/fetchSuppliers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await companyService.getSuppliers();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch suppliers');
    }
  }
);

export const fetchBuyers = createAsyncThunk(
  'companies/fetchBuyers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await companyService.getBuyers();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch buyers');
    }
  }
);

export const fetchCompanies = createAsyncThunk(
  'companies/fetchAll',
  async (type: string | undefined, { rejectWithValue }) => {
    try {
      const response = await companyService.getAll(type);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch companies');
    }
  }
);

const companySlice = createSlice({
  name: 'companies',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch suppliers
      .addCase(fetchSuppliers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSuppliers.fulfilled, (state, action) => {
        state.loading = false;
        state.suppliers = action.payload;
      })
      .addCase(fetchSuppliers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch buyers
      .addCase(fetchBuyers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBuyers.fulfilled, (state, action) => {
        state.loading = false;
        state.buyers = action.payload;
      })
      .addCase(fetchBuyers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch all companies
      .addCase(fetchCompanies.fulfilled, (state, action) => {
        state.companies = action.payload;
      });
  },
});

export const { clearError } = companySlice.actions;
export default companySlice.reducer;